<template>
  <div class="admin-user-detail bg-white-custom border px-3 py-3">
    <b-overlay :show="isLoading" rounded="sm">
      <div
        v-if="userId && currentUser && currentUser.user_id"
        class="admin-user-detail--header d-flex align-items-center justify-content-between flex-wrap gap-2"
      >
        <h2 class="mb-0 text-nowrap">
          {{ `${currentUser.fname} ${currentUser.lname}` }}
        </h2>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <span
            v-for="role in currentUser.user_roles"
            :key="`${currentUser.user_id}_${role}`"
            :class="`${role} badge float-right font-weight-medium text-capitalize`"
          >
            {{ role }}
          </span>
          <span class="badge badge-primary float-right font-weight-medium text-capitalize">
            {{ `${getProvider(currentUser.federated_id)}(Provider)` }}
          </span>
        </div>
      </div>
      <hr v-if="userId && currentUser && currentUser.user_id" />
      <ConveyRequiredStatement class="mb-2" />
      <div v-if="errorMsg" class="text-danger mb-2">{{ errorMsg }}</div>
      <div class="admin-user-detail--body">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-1"
              label="First name:"
              style="font-weight:500"
              label-for="input-fname"
              class="required-form-field"
            >
              <b-form-input
                id="input-fname"
                v-model="currentUser['fname']"
                trim
              />
              <b-form-invalid-feedback :state="!!errors['fname'] ? false : true">
                {{ errors['fname'] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-2"
              label="Last name:"
              style="font-weight:500"
              label-for="input-lname"
              class="required-form-field"
            >
              <b-form-input
                id="input-lname"
                v-model="currentUser['lname']"
                trim
              />
              <b-form-invalid-feedback :state="!!errors['lname'] ? false : true">
                {{ errors['lname'] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              id="fieldset-3"
              label="Email Address:"
              label-for="input-email"
              style="font-weight:500"
              class="required-form-field"
            >
              <b-form-input
                id="input-email"
                v-model="currentUser['email']"
                :disabled="!!currentUser && !!currentUser.user_id"
                trim
              />
              <b-form-invalid-feedback :state="!!errors['email'] ? false : true">
                {{ errors['email'] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              label="Roles:"
              style="font-weight:500"
              class="required-form-field"
            >
              <ConveyCheckboxGroup
                v-model="currentUser['user_roles']"
                :options="availableRoles"
              />
              <b-form-invalid-feedback :state="!!errors['user_roles'] ? false : true">
                {{ errors['user_roles'] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <hr />
      <div class="admin-user-detail--footer d-flex align-items-center justify-content-end">
        <b-button
          variant="success"
          pill
          size="sm"
          :disabled="savePending || deletePending"
          @click="saveUser()"
        >
          <b-spinner v-if="savePending" small></b-spinner>
          <span v-else>
            {{ currentUser && currentUser.user_id ? 'Save' : 'Add' }}
          </span>
        </b-button>
        <b-button
          v-if="currentUser && currentUser.user_id"
          pill
          variant="danger"
          size="sm"
          :disabled="savePending || deletePending"
          @click="removeUser()"
        >
          <b-spinner v-if="deletePending" small></b-spinner>
          <span v-else>Delete</span>
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import errorHandler from "@/mixins/errorHandler";
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";
import ConveyCheckboxGroup from "@/components/ConveyCheckboxGroup";

export default {
  name: "AdminUserDetail",
  mixins: [errorHandler],
  components: { ConveyRequiredStatement, ConveyCheckboxGroup },
  props: {
    userId: {
      type: String,
      default: "",
    }
  },
  data() {
    return {
      isLoading: false,
      currentUser: {},
      availableRoles: [],
      errors: {},
      errorMsg: null,
      deletePending: false,
      savePending: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgencyId", "currentAgency"]),
    error: function () {
      console.log(JSON.stringify(this.errors));
      if (this.errors && Object.keys(this.errors).length > 0)
        return "Invalid input. Please correct the errors below and try again.";
      return false;
    }
  },
  created() {
    try {
      this.availableRoles = this.currentAgency.roles.map(r => ({ text: r.role, value: r.role }));
    } catch (err) { }

    if (!this.userId) {
      this.currentUser = {};
    } else {
      this.fetchUser();
    }
  },
  methods: {
    validation() {
      return false;
    },
    hasErrors(field) {
      if (this.errors[field])
        return "text-danger";
      return "";
    },
    async saveUser() {
      this.errorMsg = null;
      this.errors = {};

      if (!this.currentUser.fname || this.currentUser.fname == "")
        this.errors["fname"] = "First name is required";
      if (!this.currentUser.lname || this.currentUser.lname == "")
        this.errors["lname"] = "Last name is required";
      if (!this.currentUser.email || this.currentUser.email == "" || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(this.currentUser.email) === false)
        this.errors["email"] = (!this.currentUser.email || this.currentUser.email == "" ? "Email is required" : "Email is invalid");
      if ((this.currentUser.user_roles||[]).length < 1)
        this.errors["user_roles"] = "Role is required";


      if (Object.keys(this.errors).length > 0)
        return false;

      let payload = {
        fname: this.currentUser.fname,
        lname: this.currentUser.lname,
        email: this.currentUser.email,
        operator_id: this.currentUser.operator_id
      };

      if (this.currentUser.phone)
        payload["phone"] = this.currentUser.phone;

      if (this.currentUser.user_roles) {
        payload["roles"] = this.currentUser.user_roles.map(role => ({
          role_id: this.currentAgency.roles.filter(r => r.role == role)[0].role_id
        }));
      }

      if (this.userId) {
        try {
          this.savePending = true;
          await this.$ConveyApi.usersApi.updateUser({ userId: this.userId, payload });
          this.$toastr("success", "User has been updated.");
          this.$emit("updated");
        } catch (err) {
          console.log(err);
          this.$toastr("error", "Error encountered updating user.");
        } finally {
          this.savePending = false;
        }
      } else {
        try {
          this.savePending = true;
          const resp = await this.$ConveyApi.invitationsApi.sendInvitation({ payload });
          const invitation = resp.data.data;
          this.$toastr("success", `An invitation email has been sent to ${invitation.email}.`);
          this.$emit("created");
        } catch (err) {
          if (err.response.status === 400 && err.response.data && err.response.data.data && err.response.data.data['error']) {
            this.$toastr("warning", err.response.data.data['error']);
          } else {
            this.$toastr("error", "Error occurred adding new user to your agency");
          }
        } finally {
          this.savePending = false;
        }
      }
    },
    async removeUser() {
      if (confirm("Are you sure you want to remove this user from your agency?") != true)
        return;

      try {
        this.deletePending = true;
        await this.$ConveyApi.usersApi.deleteUser({ userId: this.userId });
        this.$toastr("success", "User has been removed from your agency.");
        this.$emit("deleted");
      } catch (err) {
        if (err.response && err.response.data && err.response.data.data)
          this.errorMsg = err.response.data.data.error;
        else
          this.errorMsg = "An error occurred";
      } finally {
        this.deletePending = false;
      }
    },
    async fetchUser() {
      try {
        this.fetchAbortController = new AbortController();
        this.isLoading = true;
        const resp = await this.$ConveyApi.usersApi.getUser({ userId: this.userId });
        this.currentUser = resp.data.data;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminUserDetail:fetchUser] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    getProvider(federatedId) {
      if (federatedId.startsWith('auth0')) {
        return 'Convey911';
      } else if (federatedId.startsWith('oauth2')) {
        const federatedIdSplits = federatedId.split('|');
        if (federatedIdSplits.length > 1) {
          return federatedIdSplits[1];
        }
      }
      return '';
    }
  },
  beforeDestroy() {
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  }
};
</script>

<style lang="scss">
.admin-user-detail {
  max-width: 800px;
  width: 100%;
  height: auto;
  border-radius: 1rem;

  &--header {
    h2 {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    span.badge.admin {
      background-color: #f3ced6 !important;
      color: #ef5350 !important;
    }

    span.badge.operator {
      background-color: #b7f1e0 !important;
      color: #0acc95 !important;
    }
  }

  &--footer {
    gap: 0.5rem;
  }

  .custom-switch .custom-control-label::after {
    top: 0.25rem;
  }
}
</style>
